import { GLOBAL_WILDCARD } from "@/features/rbac/permissions/catalog";
import {
  BACKSTOP_ROLE_ID,
  isSystemRoleId,
} from "@/features/rbac/permissions/systemRoles";
import type { RbacRole } from "@/features/rbac/schemas/rbac";
import { matches } from "@/features/rbac/utils/matches";

/**
 * Error de política del sistema de permisos. Lleva mensaje en español porque
 * se muestra tal cual en la UI.
 */
export class RbacPolicyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RbacPolicyError";
  }
}

/** Permisos sin los cuales nadie podría volver a administrar el sistema. */
const ROLE_MANAGEMENT_PERMISSIONS = ["roles:edit", "roles:assign"] as const;

/** ¿Este conjunto de permisos permite seguir administrando roles? */
export function grantsRoleManagement(permissions: readonly string[]): boolean {
  const granted = new Set(permissions);
  return ROLE_MANAGEMENT_PERMISSIONS.every((permission) => matches(granted, permission));
}

/**
 * Un rol del sistema no se puede eliminar. Existe para que los usuarios que
 * todavía dependen de `permission_admin` sigan resolviendo sus permisos.
 */
export function assertRoleIsDeletable(role: RbacRole): void {
  if (role.system || isSystemRoleId(role.id)) {
    throw new RbacPolicyError(
      `El rol "${role.name}" es del sistema y no se puede eliminar. Si no quieres que conceda nada, quítale todos sus permisos.`
    );
  }
}

/**
 * El rol de respaldo no puede perder el permiso total.
 *
 * Es la garantía dura contra el auto-bloqueo: los usuarios que aún resuelven
 * por el camino heredado no aparecen en `rbac_assignments`, así que ninguna
 * consulta puede contarlos. Mantener este rol con acceso total asegura que
 * siempre exista una vía de recuperación.
 */
export function assertBackstopRoleIntact(
  roleId: string,
  nextPermissions: readonly string[]
): void {
  if (roleId !== BACKSTOP_ROLE_ID) return;

  if (!nextPermissions.includes(GLOBAL_WILDCARD)) {
    throw new RbacPolicyError(
      "El rol Administrador debe conservar el permiso total: es lo que garantiza que nunca se pierda el acceso a la administración de roles."
    );
  }
}

/** Nadie edita sus propios roles: evita quitarse el acceso por descuido. */
export function assertNotSelfAssignment(
  actorUid: string | null,
  targetUid: string
): void {
  if (actorUid && actorUid === targetUid) {
    throw new RbacPolicyError(
      "No puedes modificar tus propios roles. Pídeselo a otra persona con permiso de asignación."
    );
  }
}

/**
 * Tras aplicar el cambio, ¿queda alguien capaz de administrar roles?
 *
 * `survivingAssignments` son las asignaciones tal como quedarían después de la
 * escritura, y `nextRoles` los roles tal como quedarían. Los portadores del rol
 * de respaldo por la vía heredada son invisibles aquí, y por eso
 * `assertBackstopRoleIntact` es la garantía real; esta comprobación es la
 * defensa adicional para los usuarios ya migrados.
 */
export function assertRoleManagementSurvives(
  survivingAssignments: readonly { roleIds: readonly string[] }[],
  nextRoles: readonly RbacRole[]
): void {
  const rolesById = new Map(nextRoles.map((role) => [role.id, role]));

  const someoneCanManage = survivingAssignments.some((assignment) => {
    const permissions = assignment.roleIds.flatMap(
      (roleId) => rolesById.get(roleId)?.permissions ?? []
    );
    return grantsRoleManagement(permissions);
  });

  if (someoneCanManage) return;

  // Nadie con asignación explícita puede administrar roles. Solo es aceptable
  // si el rol de respaldo sigue intacto, porque entonces los administradores
  // heredados mantienen la vía de recuperación.
  const backstop = rolesById.get(BACKSTOP_ROLE_ID);
  if (backstop && grantsRoleManagement(backstop.permissions)) return;

  throw new RbacPolicyError(
    "Este cambio dejaría a la plataforma sin nadie capaz de administrar roles. Asigna antes el permiso a otra persona."
  );
}
